import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { type FC, type PropsWithChildren, useEffect, useRef } from 'react';
import { Card, Col, Container, Nav, Navbar, Row } from 'react-bootstrap';
import { Trans, useTranslation } from 'react-i18next';

import {
  AuthState,
  hasPendingLoginRedirect,
  loginRedirect,
  useAuth,
  useIsRegistered,
} from '@furality/frontend-stack';
import { Button } from '@furality/furality-purpose';
import ConButton from '#/register/ConButton.tsx';
import LoginTroubleshootingSteps from '@/components/_oauth-redirect/LoginTroubleshootingSteps.tsx';
import LanguageChangerDropdown from '@/components/LanguageChangerDropdown.tsx';
import LoginWithVRChat from '@/components/LoginWithVRChat.tsx';
import { NoLayout } from '@/components/layouts/index.tsx';
import PageLoading from '@/components/loading/PageLoading.tsx';
import useFlag from '@/lib/hooks/useFlag.ts';

const BoldCenter: FC<PropsWithChildren> = ({ children }) => (
  <p className="text-center">
    <strong>{children}</strong>
  </p>
);

const NoVRChat: FC = () => {
  const { t } = useTranslation();
  const auth = useAuth();

  return (
    <div className="main-background">
      <Navbar className="position-absolute w-100">
        <Container className="px-lg-0">
          <Nav className="align-items-center ml-auto">
            <LanguageChangerDropdown />
          </Nav>
        </Container>
      </Navbar>
      <Container className="h-100vh">
        <Row className="align-items-center h-100">
          <Col md={6} className="offset-md-3">
            <Card>
              <Card.Body>
                <h2 className="text-center">{t('shared.login.title')}</h2>
                <Trans
                  i18nKey="login.discord_login_error"
                  components={{
                    p: <p className="text-center text-danger" />,
                    bold: <BoldCenter />,
                  }}
                />

                <Button block variant="secondary" className="vrchat" onClick={() => auth.logout()}>
                  {t('login.logout')}
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

const PleaseLogIn: FC = () => {
  const { t } = useTranslation();
  const auth = useAuth();

  return (
    <div className="main-background">
      <Navbar
        className="position-absolute w-100 furality-navbar bg-none"
        style={{ '--nav-height': 'auto' }}
      >
        <Container className="px-lg-0">
          <Card className="ms-auto p-2 rounded-0 rounded-bottom-4 bg-body border-0">
            <Nav className="align-items-center ml-auto">
              <LanguageChangerDropdown />
            </Nav>
          </Card>
        </Container>
      </Navbar>
      <Container className="h-100vh">
        <Row className="align-items-center h-100">
          <Col md={6} className="offset-md-3">
            <Card>
              <Card.Body>
                <h2 className="text-center">{t('shared.login.title')}</h2>
                <Trans
                  i18nKey="login.intro"
                  components={{ p: <p className="text-center" />, strong: <strong /> }}
                />
                <ConButton
                  className="vrchat w-100"
                  onClick={() =>
                    auth.login({
                      skipOrigin: true,
                    })
                  }
                >
                  <LoginWithVRChat logo="black" />
                </ConButton>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

const Index = () => {
  const navigate = useNavigate();
  const auth = useAuth();
  const reg = useIsRegistered();

  const redirectOnce = useRef(false);

  const enableExplore = useFlag('explore');
  const pending = useFlag('pending');

  // TODO: Rework this logic
  useEffect(() => {
    if (
      !redirectOnce.current &&
      auth.state === AuthState.LoggedIn &&
      auth.user.vrchatId !== undefined
    ) {
      redirectOnce.current = true;
      if (hasPendingLoginRedirect()) {
        loginRedirect((path) => void navigate({ to: path, replace: true }));
        localStorage.setItem('DONT_REDIRECT_TO_PENDING', 'true');
      } else if (!pending) {
        if (!reg.registered) {
          void navigate({ to: '/register', replace: true });
        } else if (enableExplore) {
          void navigate({ to: '/explore', replace: true });
        } else {
          void navigate({ to: '/profile', replace: true });
        }
      }
    }
  }, [auth.state, auth.user?.vrchatId, enableExplore, navigate, pending, reg.registered]);

  if (auth.state === 'loggedIn' && auth.user.vrchatId == null) {
    return <NoVRChat />;
  }

  if (auth.state === 'error') {
    return <LoginTroubleshootingSteps />;
  }

  if (auth.state === 'notLoggedIn') {
    return <PleaseLogIn />;
  }

  return <PageLoading />;
};

// biome-ignore lint/style/useComponentExportOnlyModules: tanstack route
export const Route = createFileRoute('/')({
  component: () => (
    <NoLayout title="shared.login_button">
      <Index />
    </NoLayout>
  ),
});
